import type { Loader } from "vega";

/** Thrown for every load a chart attempts — charts draw only host-injected data. */
export class ChartLoadBlockedError extends Error {
  constructor(uri: string, context?: string) {
    super(
      `Charts cannot load external resources${context ? ` (${context})` : ""}: ${uri}`,
    );
    this.name = "ChartLoadBlockedError";
  }
}

function refuse(uri: string, context?: string): Promise<never> {
  return Promise.reject(new ChartLoadBlockedError(uri, context));
}

/**
 * A Vega loader that refuses everything. Vega routes every outside reference
 * through its loader — `data.url`, image mark `url`s (via `sanitize`), and
 * `href` click-throughs (via `sanitize`) — so this is the single runtime choke
 * point that keeps a chart from fetching, beaconing, or navigating, whatever
 * its spec says.
 */
export function createBlockedLoader(): Loader {
  return {
    load: (uri, options) => refuse(uri, options?.context),
    sanitize: (uri, options) => refuse(uri, options?.context),
    http: (uri) => refuse(uri, "http"),
    file: (filename) => refuse(filename, "file"),
  };
}
